import { ApiResponseBody, PaginatedApiResponseBody, PaginatedBody, Uuid } from '@/api/types';
import { http } from '@/api/http';
import { TMDXFile } from '@/api/apis/files';
import { getOfferApiUrl } from './offer.paths';
import { BuildInfoResponse } from './build-info.types';
import { DonorInformationRequest, DonorInformationResponse } from './donor-information.types';
import { OfferDispositionRequest, OfferDispositionResponse, OptnCodesResponse } from './offer-disposition.types';
import {
  AttachDonorSummaryRequest,
  DonorSummaryAttachmentResponse,
  DonorSummaryParseResponse,
  DonorSummaryUploadUrlRequest,
  DonorSummaryUploadUrlResponse,
} from './donor-summary.types';
import {
  mapOfferServiceResponseToOffer,
  mapOffersListFiltersToServiceQuery,
  mapOfferToServiceCreateBody,
  mapOfferToServiceWriteBody,
  mapUploadedFileToServiceModel,
} from './offer.api.mappers';
import {
  AddOfferNoteRequest,
  CreateOfferRequest,
  Offer,
  OfferLinkBody,
  OfferServiceFileModel,
  OfferServiceResponse,
  OffersListFilters,
  DeclineOfferRequest,
  UpdateOfferNoteRequest,
  UpdateOfferRequest,
} from './offer.types';

const getOffersList = async (filters: OffersListFilters): Promise<PaginatedBody<Offer>> => {
  const params = mapOffersListFiltersToServiceQuery(filters);
  const res = await http.get<PaginatedApiResponseBody<OfferServiceResponse>>(getOfferApiUrl.list(), {
    params,
    paramsSerializer: { indexes: null },
  });
  const page = res.data.data;

  return {
    ...page,
    startIndex: page.startIndex ?? (page.pageIndex ?? 0) * (page.itemsPerPage ?? 0),
    items: (page.items ?? []).map(mapOfferServiceResponseToOffer),
  };
};

const getOfferById = async (offerId: Uuid): Promise<Offer> => {
  const res = await http.get<ApiResponseBody<OfferServiceResponse>>(getOfferApiUrl.byId(offerId));
  return mapOfferServiceResponseToOffer(res.data.data);
};

// `body.files` must already be uploaded (id + path resolved); the caller uploads via useUploadFile(FileType.Offer).
const createOffer = async (body: CreateOfferRequest): Promise<Uuid | null> => {
  const res = await http.post<ApiResponseBody<Uuid | null>>(getOfferApiUrl.create(), mapOfferToServiceCreateBody(body));
  return res.data.data;
};

// FULL REPLACE: the caller must send the whole entity, not a partial.
const updateOffer = async (offerId: Uuid, body: UpdateOfferRequest): Promise<void> => {
  await http.put<ApiResponseBody<void>>(getOfferApiUrl.update(offerId), mapOfferToServiceWriteBody(body));
};

const acceptOffer = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.accept(offerId));
};

const declineOffer = async (offerId: Uuid, body: DeclineOfferRequest): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.decline(offerId), body);
};

const markOfferErroneous = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.erroneous(offerId));
};

const rollbackOffer = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.rollback(offerId));
};

const markOfferAcceptedForNop = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.acceptedForNop(offerId));
};

const presentOffer = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.present(offerId));
};

const acceptOfferAhead = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.acceptAhead(offerId));
};

const autoRuleOutOffer = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.autoRuleOut(offerId));
};

const closeOffer = async (offerId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.close(offerId));
};

// The endpoint attaches ONE file per request (single entity, not an array) and returns just the
// new file's id, not the full offer.
const addOfferFile = async (offerId: Uuid, file: OfferServiceFileModel): Promise<Uuid | null> => {
  const res = await http.post<ApiResponseBody<Uuid | null>>(getOfferApiUrl.files(offerId), file);
  return res.data.data;
};

// `files` must already be uploaded (caller uploads via useUploadFile(FileType.Offer)).
// Attaches run SEQUENTIALLY (chained reduce, not parallel) so appends can't race on the last-write-wins offer.
const addOfferFiles = async (offerId: Uuid, files: TMDXFile[]): Promise<Offer> => {
  await files.reduce<Promise<void>>(
    (prev, file) => prev.then(() => addOfferFile(offerId, mapUploadedFileToServiceModel(file)).then(() => undefined)),
    Promise.resolve(),
  );
  return getOfferById(offerId);
};

const deleteOfferFile = async (offerId: Uuid, fileId: Uuid): Promise<void> => {
  await http.delete<ApiResponseBody<void>>(getOfferApiUrl.fileById(offerId, fileId));
};

// Returns just the new link's id, not the full offer.
const addOfferLink = async (offerId: Uuid, body: OfferLinkBody): Promise<Uuid | null> => {
  const res = await http.post<ApiResponseBody<Uuid | null>>(getOfferApiUrl.links(offerId), body);
  return res.data.data;
};

// The BE updates a link via POST (not PATCH).
const updateOfferLink = async (offerId: Uuid, linkId: Uuid, body: OfferLinkBody): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.linkById(offerId, linkId), body);
};

const deleteOfferLink = async (offerId: Uuid, linkId: Uuid): Promise<void> => {
  await http.delete<ApiResponseBody<void>>(getOfferApiUrl.linkById(offerId, linkId));
};

const addOfferNote = async (offerId: Uuid, body: AddOfferNoteRequest): Promise<Uuid | null> => {
  const res = await http.post<ApiResponseBody<Uuid | null>>(getOfferApiUrl.notes(offerId), body);
  return res.data.data;
};

const updateOfferNote = async (offerId: Uuid, noteId: Uuid, body: UpdateOfferNoteRequest): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.noteById(offerId, noteId), body);
};

const deleteOfferNote = async (offerId: Uuid, noteId: Uuid): Promise<void> => {
  await http.delete<ApiResponseBody<void>>(getOfferApiUrl.noteById(offerId, noteId));
};

const pinOfferNote = async (offerId: Uuid, noteId: Uuid): Promise<void> => {
  await http.post<ApiResponseBody<void>>(getOfferApiUrl.notePin(offerId, noteId));
};

const unpinOfferNote = async (offerId: Uuid, noteId: Uuid): Promise<void> => {
  await http.delete<ApiResponseBody<void>>(getOfferApiUrl.noteUnpin(offerId, noteId));
};

const getDonorInformation = async (offerId: Uuid): Promise<DonorInformationResponse> => {
  const res = await http.get<ApiResponseBody<DonorInformationResponse>>(getOfferApiUrl.donorInformation(offerId));
  return res.data.data;
};

// FULL REPLACE: the caller must send the whole confirmed donor-information state, not a partial.
const updateDonorInformation = async (offerId: Uuid, body: DonorInformationRequest): Promise<void> => {
  await http.put<ApiResponseBody<void>>(getOfferApiUrl.donorInformation(offerId), body);
};

// Creates a new offer prefilled with `offerId`'s current data - the source offer is unchanged (ADR-010).
const cloneOffer = async (offerId: Uuid): Promise<Uuid | null> => {
  const res = await http.post<ApiResponseBody<Uuid | null>>(getOfferApiUrl.clone(offerId));
  return res.data.data;
};

const getOfferDisposition = async (offerId: Uuid): Promise<OfferDispositionResponse> => {
  const res = await http.get<ApiResponseBody<OfferDispositionResponse>>(getOfferApiUrl.disposition(offerId));
  return res.data.data;
};

// FULL REPLACE: the caller must send the whole disposition, not a partial.
const updateOfferDisposition = async (offerId: Uuid, body: OfferDispositionRequest): Promise<void> => {
  await http.put<ApiResponseBody<void>>(getOfferApiUrl.disposition(offerId), body);
};

const getRefusalCodes = async (): Promise<OptnCodesResponse> => {
  const res = await http.get<ApiResponseBody<OptnCodesResponse>>(getOfferApiUrl.refusalCodes());
  return res.data.data;
};

// Step 1 of the donor-summary pipeline - the caller PUTs the raw PDF bytes to `signedUrl` directly
// (not via `http`, same as `useUploadFile` - S3 rejects our auth/tenant interceptor headers).
const createDonorSummaryUploadUrl = async (
  offerId: Uuid,
  body: DonorSummaryUploadUrlRequest,
): Promise<DonorSummaryUploadUrlResponse> => {
  const res = await http.post<ApiResponseBody<DonorSummaryUploadUrlResponse>>(
    getOfferApiUrl.donorSummaryUploadUrl(offerId),
    body,
  );
  return res.data.data;
};

// Step 2 - confirms the pre-uploaded PDF as the offer's current donor summary.
const confirmDonorSummary = async (
  offerId: Uuid,
  body: AttachDonorSummaryRequest,
): Promise<DonorSummaryAttachmentResponse> => {
  const res = await http.put<ApiResponseBody<DonorSummaryAttachmentResponse>>(
    getOfferApiUrl.donorSummary(offerId),
    body,
  );
  return res.data.data;
};

const getDonorSummary = async (offerId: Uuid): Promise<DonorSummaryAttachmentResponse | null> => {
  const res = await http.get<ApiResponseBody<DonorSummaryAttachmentResponse | null>>(
    getOfferApiUrl.donorSummary(offerId),
  );
  return res.data.data;
};

// Step 3 - starts an async Textract parse of the offer's current donor summary, returns the parseId
// the caller polls. No request body - the target is always the current summary.
const startDonorSummaryParse = async (offerId: Uuid): Promise<Uuid | null> => {
  const res = await http.post<ApiResponseBody<Uuid | null>>(getOfferApiUrl.donorSummaryParse(offerId));
  return res.data.data;
};

// Step 4 - poll target. Omitting `parseId` restores the offer's latest parse (used to hydrate state
// when Step 2 mounts without an in-memory parseId, e.g. after a remount).
const getDonorSummaryParse = async (offerId: Uuid, parseId?: Uuid): Promise<DonorSummaryParseResponse> => {
  const url = parseId
    ? getOfferApiUrl.donorSummaryParseById(offerId, parseId)
    : getOfferApiUrl.donorSummaryParse(offerId);
  const res = await http.get<ApiResponseBody<DonorSummaryParseResponse>>(url);
  return res.data.data;
};

// No offerId - reports on the currently-deployed OfferService build itself, not any one offer.
const getBuildInfo = async (): Promise<BuildInfoResponse> => {
  const res = await http.get<ApiResponseBody<BuildInfoResponse>>(getOfferApiUrl.buildInfo());
  return res.data.data;
};

export const offerApiFetcher = {
  getOffersList,
  getOfferById,
  createOffer,
  updateOffer,
  cloneOffer,
  acceptOffer,
  declineOffer,
  markOfferErroneous,
  rollbackOffer,
  markOfferAcceptedForNop,
  presentOffer,
  acceptOfferAhead,
  autoRuleOutOffer,
  closeOffer,
  addOfferFile,
  addOfferFiles,
  deleteOfferFile,
  addOfferLink,
  updateOfferLink,
  deleteOfferLink,
  addOfferNote,
  updateOfferNote,
  deleteOfferNote,
  pinOfferNote,
  unpinOfferNote,
  getDonorInformation,
  updateDonorInformation,
  getOfferDisposition,
  updateOfferDisposition,
  getRefusalCodes,
  createDonorSummaryUploadUrl,
  confirmDonorSummary,
  getDonorSummary,
  startDonorSummaryParse,
  getDonorSummaryParse,
  getBuildInfo,
};
